import React, { useState, useEffect } from "react";
import { 
  FiX, 
  FiTag, 
  FiCalendar, 
  FiClock, 
  FiUpload, 
  FiImage, 
  FiVideo,
  FiLayers,
  FiCheckSquare,
  FiSquare,
  FiRadio,
  FiSave,
  FiLoader,
  FiAlertCircle,
  FiCheckCircle,
  FiMapPin,
  FiFile,
  FiRefreshCw
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineCampaign,
  MdOutlineDateRange,
  MdOutlineAccessTime,
  MdOutlineUpload,
  MdOutlineImage,
  MdOutlineVideoLibrary,
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank,
  MdOutlineRadioButtonChecked,
  MdOutlineRadioButtonUnchecked,
  MdOutlineSave,
  MdOutlineLocationOn
} from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import InputField from "../../../components/ui/Input";
import axios from "axios";
import { baseURL } from "../../../BaseURL";

const POSITIONS = ["post", "forum-post", "main"];

const PLACEMENT_OPTIONS = {
  post: ["top", "middle", "bottom"],
  "forum-post": ["top", "middle", "bottom"],
  main: [
    "main",
    "top_performance",
    "bottom_of_learning_batches",
    "bottom_of_free_batches",
    "bottom_of_trending_courses",
    "bottom_of_one_on_one",
    "bottom_of_free_courses",
    "bottom_of_featured_experts",
    "bottom_of_subscription",
    "bottom_of_recently_viewed",
  ],
};

const formatLabel = (str) =>
  str
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const CreateNewAdvertisement = ({
  isOpen,
  onClose,
  data,
  setData,
  onSubmit,
}) => {
  const [imagePreview, setImagePreview] = useState(data?.imageUrl || "");
  const [videoPreview, setVideoPreview] = useState(data?.videoUrl || "");
  const [imageLoading, setImageLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isImageDragging, setIsImageDragging] = useState(false);
  const [isVideoDragging, setIsVideoDragging] = useState(false);

  const mediaType = data?.mediaType || "image";

  const mediaTypeOptions = [
    { label: "Image", value: "image" },
    { label: "Video", value: "video" },
  ];

  const selectedPositions = data?.positions || [];
  const placementConfig = data?.placementConfig || {};

  useEffect(() => {
    setImagePreview(data?.imageUrl || "");
    setVideoPreview(data?.videoUrl || "");
  }, [data?.imageUrl, data?.videoUrl]);

  useEffect(() => {
    if (!data?.mediaType) {
      setData((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          mediaType: "image",
        },
      }));
    }
  }, [data?.mediaType, setData]);

  const handleMediaTypeChange = (val) => {
    setError(null);
    setData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        mediaType: val,
      },
    }));
  };

  const handlePositionToggle = (position) => {
    const isSelected = selectedPositions.includes(position);
    const newPositions = isSelected
      ? selectedPositions.filter((p) => p !== position)
      : [...selectedPositions, position];

    const newPlacementConfig = { ...placementConfig };
    if (isSelected) {
      delete newPlacementConfig[position];
    }

    setData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        positions: newPositions,
        placementConfig: newPlacementConfig,
      },
    }));
  };

  const handlePlacementChange = (position, value) => {
    setData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        placementConfig: {
          ...prev.data?.placementConfig,
          [position]: value,
        },
      },
    }));
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    if (type === "image") {
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file (JPG, PNG, WebP, etc.)");
        return;
      }
      setError(null);
      setImagePreview(URL.createObjectURL(file));

      const formData = new FormData();
      formData.append("image", file);

      try {
        setImageLoading(true);
        const response = await axios.post(
          `${baseURL}/v1/user/uploadImage`,
          formData,
        );
        if (response?.data?.data?.imageURL) {
          setData((prev) => ({
            ...prev,
            data: { ...prev.data, imageUrl: response.data.data.imageURL },
          }));
        } else {
          throw new Error("No image URL received from server");
        }
      } catch (err) {
        console.error("Error uploading image:", err);
        setError(
          err.response?.data?.message ||
            "Failed to upload image. Please try again.",
        );
      } finally {
        setImageLoading(false);
      }
    } else if (type === "video") {
      if (!file.type.startsWith("video/")) {
        setError("Please select a valid video file (MP4, MOV, WebM, etc.)");
        return;
      }
      setError(null);
      setVideoPreview(URL.createObjectURL(file));

      const formData = new FormData();
      formData.append("image", file);

      try {
        setVideoLoading(true);
        const response = await axios.post(
          `${baseURL}/v1/user/uploadImage`,
          formData,
        );
        if (response?.data?.data?.imageURL) {
          setData((prev) => ({
            ...prev,
            data: { ...prev.data, videoUrl: response.data.data.imageURL },
          }));
        } else {
          throw new Error("No video URL received from server");
        }
      } catch (err) {
        console.error("Error uploading video:", err);
        setError(
          err.response?.data?.message ||
            "Failed to upload video. Please try again.",
        );
      } finally {
        setVideoLoading(false);
      }
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, "image");
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, "video");
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsImageDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file, "image");
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    setIsVideoDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file, "video");
  };

  const handleRemoveImage = () => {
    setImagePreview("");
    setData((prev) => ({
      ...prev,
      data: { ...prev.data, imageUrl: "" },
    }));
  };

  const handleRemoveVideo = () => {
    setVideoPreview("");
    setData((prev) => ({
      ...prev,
      data: { ...prev.data, videoUrl: "" },
    }));
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    const errors = [];
    if (!data?.title?.trim()) errors.push("Title is required");
    if (!data?.startDate) errors.push("Start date is required");
    if (!data?.endDate) errors.push("End date is required");
    if (
      data?.startDate &&
      data?.endDate &&
      new Date(data.endDate) < new Date(data.startDate)
    )
      errors.push("End date must be after start date");
    if (!data?.period || data.period <= 0)
      errors.push("Invalid number of days");

    if (mediaType === "image") {
      if (!data?.imageUrl) errors.push("Please upload an advertisement image");
    } else if (mediaType === "video") {
      if (!data?.imageUrl) errors.push("Please upload a thumbnail image");
      if (!data?.videoUrl) errors.push("Please upload an advertisement video");
    }

    if (!selectedPositions.length)
      errors.push("Please select at least one position");
    const missingPlacement = selectedPositions.filter(
      (p) => !placementConfig[p],
    );
    if (missingPlacement.length)
      errors.push(
        `Please select placement for: ${missingPlacement.map(formatLabel).join(", ")}`,
      );

    if (errors.length) {
      setError(errors);
      return;
    }
    setError(null);
    onSubmit(e);
  };

  useEffect(() => {
    if (data?.startDate && data?.endDate) {
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setData((prev) => ({
        ...prev,
        data: { ...prev.data, period: diffDays > 0 ? diffDays : 0 },
      }));
    }
  }, [data?.startDate, data?.endDate, setData]);

  if (!isOpen) return null;

  const isMediaValid =
    mediaType === "image"
      ? !!data?.imageUrl
      : !!(data?.imageUrl && data?.videoUrl);

  // Check if form is valid
  const isFormValid =
    data?.title?.trim() &&
    data?.startDate &&
    data?.endDate &&
    isMediaValid &&
    selectedPositions.length > 0 &&
    selectedPositions.every((p) => placementConfig[p]);

  const isUploading = imageLoading || videoLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <MdOutlineCampaign className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {!data?._id ? "Create New Advertisement" : "Update Advertisement"}
              </h3>
              <p className="text-xs text-gray-400 hidden sm:block">
                {!data?._id ? 'Create a new advertisement campaign' : 'Update advertisement details'}
              </p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onClose}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-3 p-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl animate-in fade-in duration-200">
              <FiAlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              {Array.isArray(error) ? (
                <ul className="list-disc list-inside space-y-1 flex-1">
                  {error.map((err, i) => (
                    <li key={i} className="text-sm">{err}</li>
                  ))}
                </ul>
              ) : (
                <p className="flex-1">{error}</p>
              )}
              <button
                onClick={() => setError(null)}
                className="text-rose-400 hover:text-rose-600 transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiTag className="w-4 h-4 text-gray-400" />
              <span>Title</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={data?.title || ""}
              onChange={(e) =>
                setData((prev) => ({
                  ...prev,
                  data: { ...prev.data, title: e.target.value },
                }))
              }
              placeholder="Enter advertisement title..."
              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                <FiCalendar className="w-4 h-4 text-gray-400" />
                <span>Start Date</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={data?.startDate || ""}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    data: { ...prev.data, startDate: e.target.value },
                  }))
                }
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                <FiCalendar className="w-4 h-4 text-gray-400" />
                <span>End Date</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={data?.endDate || ""}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    data: { ...prev.data, endDate: e.target.value },
                  }))
                }
                min={data?.startDate || new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Days & Media Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                <FiClock className="w-4 h-4 text-gray-400" />
                <span>Duration (Days)</span>
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="period"
                value={data?.period || ""}
                min="1"
                disabled
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed focus:outline-none"
              />
              <p className="mt-1 text-xs text-gray-400">Auto-calculated from date range</p>
            </div>

            <div>
              <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                <FiLayers className="w-4 h-4 text-gray-400" />
                <span>Media Type</span>
                <span className="text-red-500">*</span>
              </label>
              <select
                name="mediaType"
                value={mediaType}
                onChange={(e) => handleMediaTypeChange(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200 cursor-pointer"
              >
                {mediaTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Media Upload Section */}
          {mediaType === "image" ? (
            /* Single Image Upload */
            <div>
              <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                <FiImage className="w-4 h-4 text-gray-400" />
                <span>Advertisement Image</span>
                <span className="text-red-500">*</span>
              </label>
              
              <div
                onDragOver={(e) => { e.preventDefault(); setIsImageDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsImageDragging(false); }}
                onDrop={handleImageDrop}
                className={`
                  relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200
                  ${isImageDragging 
                    ? 'border-[#715EFE] bg-[#715EFE]/5' 
                    : 'border-gray-300 hover:border-[#715EFE] hover:bg-gray-50/50'
                  }
                  ${imageLoading ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={imageLoading}
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="p-3 bg-gray-50 rounded-full">
                    <FiUpload className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">
                    {isImageDragging ? 'Drop your image here' : 'Drag & drop or click to upload advertisement image'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Supports PNG, JPG, JPEG, WebP
                  </p>
                </div>
              </div>

              {imageLoading && (
                <div className="flex items-center gap-3 mt-3 p-3 bg-gray-50 rounded-lg">
                  <FiLoader className="w-5 h-5 text-[#715EFE] animate-spin" />
                  <span className="text-sm text-gray-600">Uploading advertisement image...</span>
                </div>
              )}

              {imagePreview && (
                <div className="mt-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img
                        src={imagePreview}
                        alt="Advertisement Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        Advertisement Image
                      </p>
                      <p className="text-xs text-emerald-600 font-medium">
                        Uploaded successfully
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all duration-200"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Video Mode: Thumbnail + Video Uploads */
            <div className="space-y-4">
              {/* Option 1: Thumbnail Upload */}
              <div>
                <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                  <FiImage className="w-4 h-4 text-gray-400" />
                  <span>Thumbnail Image</span>
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 font-normal">(Cover image for video)</span>
                </label>
                
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsImageDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsImageDragging(false); }}
                  onDrop={handleImageDrop}
                  className={`
                    relative border-2 border-dashed rounded-xl p-5 text-center transition-all duration-200
                    ${isImageDragging 
                      ? 'border-[#715EFE] bg-[#715EFE]/5' 
                      : 'border-gray-300 hover:border-[#715EFE] hover:bg-gray-50/50'
                    }
                    ${imageLoading ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={imageLoading}
                  />
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="p-2.5 bg-gray-50 rounded-full">
                      <FiUpload className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">
                      {isImageDragging ? 'Drop thumbnail here' : 'Upload video thumbnail image'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Supports PNG, JPG, JPEG, WebP
                    </p>
                  </div>
                </div>

                {imageLoading && (
                  <div className="flex items-center gap-3 mt-2.5 p-2.5 bg-gray-50 rounded-lg">
                    <FiLoader className="w-4 h-4 text-[#715EFE] animate-spin" />
                    <span className="text-xs text-gray-600">Uploading thumbnail...</span>
                  </div>
                )}

                {imagePreview && (
                  <div className="mt-2.5 p-2 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={imagePreview}
                          alt="Thumbnail Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          Thumbnail Image
                        </p>
                        <p className="text-xs text-emerald-600 font-medium">
                          Uploaded successfully
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all duration-200"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Option 2: Video Upload */}
              <div>
                <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                  <FiVideo className="w-4 h-4 text-gray-400" />
                  <span>Advertisement Video</span>
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 font-normal">(Video file)</span>
                </label>
                
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsVideoDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsVideoDragging(false); }}
                  onDrop={handleVideoDrop}
                  className={`
                    relative border-2 border-dashed rounded-xl p-5 text-center transition-all duration-200
                    ${isVideoDragging 
                      ? 'border-[#715EFE] bg-[#715EFE]/5' 
                      : 'border-gray-300 hover:border-[#715EFE] hover:bg-gray-50/50'
                    }
                    ${videoLoading ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={videoLoading}
                  />
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="p-2.5 bg-gray-50 rounded-full">
                      <FiVideo className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">
                      {isVideoDragging ? 'Drop video here' : 'Upload advertisement video'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Supports MP4, MOV, WebM, etc.
                    </p>
                  </div>
                </div>

                {videoLoading && (
                  <div className="flex items-center gap-3 mt-2.5 p-2.5 bg-gray-50 rounded-lg">
                    <FiLoader className="w-4 h-4 text-[#715EFE] animate-spin" />
                    <span className="text-xs text-gray-600">Uploading video...</span>
                  </div>
                )}

                {videoPreview && (
                  <div className="mt-2.5 p-2 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-14 rounded-lg overflow-hidden bg-black flex-shrink-0">
                        <video
                          src={videoPreview}
                          className="w-full h-full object-cover"
                          controls
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          Advertisement Video
                        </p>
                        <p className="text-xs text-emerald-600 font-medium">
                          Uploaded successfully
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveVideo}
                        className="p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all duration-200"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Positions */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FiMapPin className="w-4 h-4 text-gray-400" />
              <span>Positions</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {POSITIONS.map((position) => {
                const isSelected = selectedPositions.includes(position);
                return (
                  <label
                    key={position}
                    className="flex items-center gap-2 cursor-pointer select-none group"
                  >
                    <button
                      type="button"
                      onClick={() => handlePositionToggle(position)}
                      className="focus:outline-none"
                    >
                      {isSelected ? (
                        <FiCheckSquare className="w-5 h-5 text-[#715EFE]" />
                      ) : (
                        <FiSquare className="w-5 h-5 text-gray-400 group-hover:text-[#715EFE] transition-colors" />
                      )}
                    </button>
                    <span className={`text-sm ${isSelected ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                      {formatLabel(position)}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Placement Config */}
          {selectedPositions.length > 0 && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FiRadio className="w-4 h-4 text-gray-400" />
                <span>Placement Configuration</span>
                <span className="text-red-500">*</span>
              </label>
              {selectedPositions.map((position) => (
                <div
                  key={position}
                  className="border border-gray-200 rounded-xl p-4 bg-gray-50/70 space-y-3 transition-all duration-200"
                >
                  <p className="text-sm font-semibold text-[#715EFE] flex items-center gap-2">
                    <FiMapPin className="w-4 h-4" />
                    {formatLabel(position)}
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {PLACEMENT_OPTIONS[position].map((option) => {
                      const isSelected = placementConfig[position] === option;
                      return (
                        <label
                          key={option}
                          className="flex items-center gap-2 cursor-pointer select-none group"
                        >
                          <button
                            type="button"
                            onClick={() => handlePlacementChange(position, option)}
                            className="focus:outline-none"
                          >
                            {isSelected ? (
                              <MdOutlineRadioButtonChecked className="w-5 h-5 text-[#715EFE]" />
                            ) : (
                              <MdOutlineRadioButtonUnchecked className="w-5 h-5 text-gray-400 group-hover:text-[#715EFE] transition-colors" />
                            )}
                          </button>
                          <span className={`text-sm ${isSelected ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                            {formatLabel(option)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid && !isUploading
                ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5'
                : 'bg-gray-300 cursor-not-allowed opacity-70'
            }`}
            onClick={handleSubmitForm}
            disabled={!isFormValid || isUploading}
          >
            {isUploading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4" />
                <span>{!data?._id ? 'Create Ad' : 'Update Ad'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewAdvertisement;